import { useQuery } from '@tanstack/react-query'

import { supabase } from '@/lib/supabase/cliente'
import { mensajeDeError } from '@/lib/supabase/errores'

export type UnidadParaEtiqueta = {
  id: string
  etiqueta: string
  token: string
  herramienta: string
  codigo: string
}

/**
 * Las unidades con su token, para imprimir.
 *
 * SOLO LAS VE QUIEN PUEDE EDITAR HERRAMIENTAS. No es una decisión de esta
 * pantalla sino de la política de la base: al operario la consulta le
 * devuelve cero filas. Con los veinticinco tokens en la mano y estando en la
 * bodega —donde va a estar de todos modos— podría marcar como devuelta la
 * escalera de un compañero que sigue en la calle.
 */
export function useUnidadesParaEtiqueta() {
  const consulta = useQuery({
    queryKey: ['herramientas', 'unidades', 'etiquetas'],
    queryFn: async (): Promise<UnidadParaEtiqueta[]> => {
      const { data, error } = await supabase
        .from('unidades_herramienta')
        .select('id, etiqueta, token, herramienta:herramientas!inner(nombre, codigo)')
        .eq('activa', true)
        .order('etiqueta')

      if (error) throw new Error(mensajeDeError(error))

      return (data ?? []).map((u) => {
        // Con !inner la relación es un objeto, pero PostgREST la tipa como si
        // pudiera ser lista. Se normaliza aquí y no en cada uso.
        const h = Array.isArray(u.herramienta) ? u.herramienta[0] : u.herramienta
        return {
          id: u.id,
          etiqueta: u.etiqueta,
          token: u.token,
          herramienta: h?.nombre ?? '',
          codigo: h?.codigo ?? '',
        }
      })
    },
  })

  return {
    unidades: consulta.data ?? [],
    cargando: consulta.isLoading,
    error: consulta.error?.message ?? null,
  }
}

/**
 * Lo que va DENTRO del QR: la dirección que devuelve esta herramienta.
 *
 * Es una dirección y no el token pelado, y esa es la decisión que ahorra
 * programar un lector: la cámara de cualquier teléfono reconoce una
 * dirección, la ofrece de un toque y abre la aplicación en la pantalla de
 * devolver. Con el token pelado haría falta un escáner dentro de la app —más
 * código, permisos de cámara que pedir, y un técnico aprendiendo dónde está
 * el botón.
 *
 * Sale de `location.origin` y no de un dominio escrito a mano: así la misma
 * etiqueta sirve en local y en producción sin tocar nada.
 */
export function direccionDelQr(token: string): string {
  return `${window.location.origin}/devolver/${token}`
}
